/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import DatePicker from "react-datepicker";
import { DebounceInput } from "react-debounce-input";
import { LayoutContext } from "../../components/layout";
import { addTimeToDateString } from "../../utils/inject";
import {
  statuses,
  deviceTypes,
  deviceManufacturers,
  thirtyDayStatuses,
  interrogationTypes,
  insuranceTypes,
  interrogationAccessHistories,
  reportAlertTypeOptions,
} from "../../constants/AdvancedFilterConstants";
import MultiSelectV2 from "../../components/MultiSelect/MultiSelectV2";

const AllReportsFilter = ({ onFilterChange, filter, tab }) => {
  const { user, readPhysicians } = useContext(LayoutContext);
  const [physicians, setPhysicians] = useState([]);

  useEffect(() => {
    if (readPhysicians) setPhysicians(readPhysicians);
  }, [readPhysicians]);

  const onChange = (e) => {
    const elem = e.target;
    const dataSet = elem.dataset;
    const tfilter = { ...filter };
    if (elem.value) {
      tfilter[dataSet.key] = { ...dataSet, value: elem.value };
    } else if (tfilter[dataSet.key]) delete tfilter[dataSet.key];
    onFilterChange({ ...tfilter });
  };

  const onChangeMultiSelect = (value, actionMeta) => {
    const tfilter = { ...filter };
    switch (actionMeta.action) {
      case "select-option":
        tfilter[actionMeta.name] = {
          key: actionMeta.name,
          com: "$lk",
          value: value.map(({ value }) => value).join(","),
        };
        break;
      case "remove-value":
        if (value.length) {
          tfilter[actionMeta.name].value = tfilter[actionMeta.name].value
            .split(",")
            .filter((val) => val !== actionMeta.removedValue.value)
            .join(",");
        } else delete tfilter[actionMeta.name];
        break;
      default:
        delete tfilter[actionMeta.name];
        break;
    }

    onFilterChange({ ...tfilter });
  };

  const physiciansDisplayInfo = (field) => {
    const physiciansList = physicians
      .filter((i) => i.rtDefibrillators || i.rtPaceMakers)
      .sort((a, b) => {
        if (a.family > b.family) return 1;
        if (b.family > a.family) return -1;
        return 0;
      });

    return (
      <>
        <option value="">Search by {field}</option>
        {physiciansList.map((item) => (
          <option key={item.id} value={item.id}>{`${item.family}, ${item.name} (${item.username})`}</option>
        ))}
      </>
    );
  };

  const showFilteredInfo = (data, selectedData, field) => {
    const filteredData = data.filter((item) => item != selectedData);
    return (
      <>
        <option value={selectedData}>{selectedData}</option>
        <option value="">Search by {field}...</option>
        {filteredData.map((item) => (
          <option value={item} key={item}>
            {item}
          </option>
        ))}
      </>
    );
  };

  const showLocations = (data, selectedData, field) => {
    const filteredData = data.filter((item) => {
      if (field === "Location") {
        return item.name !== selectedData;
      } else return item !== selectedData;
    });
    return (
      <>
        <option value={selectedData}>{selectedData}</option>
        <option value="">Search by {field}...</option>
        {filteredData.map((item) => {
          if (field === "Location") {
            return (
              <option value={item.name} key={item.name}>
                {item.name}
              </option>
            );
          } else
            return (
              <option value={item} key={item}>
                {item}
              </option>
            );
        })}
      </>
    );
  };

  const getPriorityFlagValues = () => {
    const filterPriorityFlag = (filter && filter["priorityFlag"]?.value) || "";
    const result = filterPriorityFlag
      .split(",")
      .filter((val) => val)
      .map((val) => {
        const alertType = reportAlertTypeOptions.find((alertType) => alertType.value === val);
        return { value: val, label: alertType.label };
      });

    return result;
  };

  return (
    <form className="search toggleSearch" id="searchContent">
      <div className="advOpt">
        <div className="row rowStretch row-cols-5">
          <div className="col">
            <h2>Patient</h2>
            <div className="row">
              <label className="inCal">
                <span>Last Name</span>
                <DebounceInput
                  id="last-name-filter"
                  debounceTimeout={800}
                  type="text"
                  placeholder="Search by Last Name..."
                  value={(filter && filter["patient.family"]?.value) || ""}
                  onChange={onChange}
                  data-key="patient.family"
                  data-com="$lk"
                />
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>First Name</span>
                <DebounceInput
                  id="first-name-filter"
                  debounceTimeout={800}
                  type="text"
                  placeholder="Search by First Name..."
                  value={(filter && filter["patient.name"]?.value) || ""}
                  onChange={onChange}
                  data-key="patient.name"
                  data-com="$lk"
                />
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Date of Birth</span>
                <DatePicker
                  id="date-of-birth-filter"
                  placeholder="Search by Date of Birth..."
                  selected={
                    (filter &&
                      filter["patient.dob"]?.value &&
                      new Date(addTimeToDateString(filter["patient.dob"]?.value))) ||
                    ""
                  }
                  onChange={(date) => {
                    const tfilter = { ...filter };
                    tfilter["patient.dob"] = { com: "$eq", key: "patient.dob", value: date.toStandardDate() };
                    onFilterChange({ ...tfilter });
                  }}
                />
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Medical Record Number</span>
                <DebounceInput
                  id="medical-record-number-filter"
                  debounceTimeout={800}
                  type="text"
                  placeholder="Search by Medical Record Number..."
                  value={(filter && filter["mrn"]?.value) || ""}
                  onChange={onChange}
                  data-key="mrn"
                  data-com="$lk"
                  key={`${tab}-mrn`}
                />
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Visit Number</span>
                <DebounceInput
                  id="visit-number-filter"
                  debounceTimeout={800}
                  type="text"
                  placeholder="Search by Device Visit Number..."
                  value={(filter && filter["visitNumber"]?.value) || ""}
                  onChange={onChange}
                  data-key="visitNumber"
                  data-com="$lk"
                  key={`${tab}-visitNumber`}
                />
              </label>
            </div>
          </div>
          <div className="col">
            <h2>Clinic</h2>
            <div className="row">
              <label className="inCal">
                <span>Location</span>
                <select
                  id="location-filter"
                  key={`${tab}-loc`}
                  onChange={onChange}
                  data-key="location.name"
                  data-com="$eq"
                >
                  {filter && filter["location.name"]?.value ? (
                    showLocations(user.locations, filter["location.name"]?.value, "Location")
                  ) : (
                    <>
                      <option value="">Search by Location...</option>
                      {user.locations.map((location) => (
                        <option key={location.name} value={location.name}>
                          {location.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Sub-location</span>
                <select
                  id="sub-location-filter"
                  key={`${tab}-subLoc`}
                  onChange={onChange}
                  data-key="patient.subLocation"
                  data-com="$eq"
                >
                  {filter && filter["patient.subLocation"]?.value ? (
                    showLocations(
                      JSON.parse(user.locations[0].subLocations),
                      filter["patient.subLocation"]?.value,
                      "Sub-location"
                    )
                  ) : (
                    <>
                      <option value="">Search by Sub-location...</option>
                      {user.locations[0].subLocations &&
                        JSON.parse(user.locations[0].subLocations).map((subLocation) => (
                          <option key={subLocation} value={subLocation}>
                            {subLocation}
                          </option>
                        ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Following Physician</span>
                <select
                  id="following-physician-filter"
                  key={`${tab}-following`}
                  onChange={onChange}
                  data-key="patient.followingPhysician.id"
                  data-com="$eq"
                  value={(filter && filter["patient.followingPhysician.id"]?.value) || ""}
                >
                  {physiciansDisplayInfo("Following Physician")}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Assigned To</span>
                <select
                  id="assigned-to-filter"
                  key={`${tab}-assigned`}
                  onChange={onChange}
                  data-key="readingPhysician.id"
                  data-com="$eq"
                  value={(filter && filter["readingPhysician.id"]?.value) || ""}
                >
                  {physiciansDisplayInfo("Assigned To")}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Transmission Type</span>
                <select
                  id="type-filter"
                  key={`${tab}-type`}
                  onChange={onChange}
                  data-key="interrogationType"
                  data-com="$eq"
                >
                  {filter && filter["interrogationType"]?.value ? (
                    showFilteredInfo(interrogationTypes, filter["interrogationType"]?.value, "Type")
                  ) : (
                    <>
                      <option value="">Search by Type...</option>
                      {interrogationTypes.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
          </div>
          <div className="col">
            <h2>Device</h2>
            <div className="row">
              <label className="inCal">
                <span>Device Manufacturer</span>
                <select
                  id="device-manufacturer-filter"
                  key={`${tab}-devManufacturer`}
                  onChange={onChange}
                  data-key="device.manufacturerName"
                  data-com="$eq"
                >
                  {filter && filter["device.manufacturerName"]?.value ? (
                    showFilteredInfo(
                      deviceManufacturers,
                      filter["device.manufacturerName"]?.value,
                      "Device Manufacturer"
                    )
                  ) : (
                    <>
                      <option value="">Search by Device Manufacturer...</option>
                      {deviceManufacturers.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Device Type</span>
                <select
                  id="device-type-filter"
                  key={`${tab}-devType`}
                  onChange={onChange}
                  data-key="device.deviceType"
                  data-com="$eq"
                >
                  {filter && filter["device.deviceType"]?.value ? (
                    showFilteredInfo(deviceTypes, filter["device.deviceType"]?.value, "Device Type")
                  ) : (
                    <>
                      <option value="">Search by Device Type...</option>
                      {deviceTypes.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Device Model</span>
                <DebounceInput
                  id="device-model-filter"
                  debounceTimeout={800}
                  type="text"
                  placeholder="Search by Device Model..."
                  value={(filter && filter["device.modelName"]?.value) || ""}
                  onChange={onChange}
                  data-key="device.modelName"
                  data-com="$lk"
                  key={`${tab}-devModel`}
                />
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Device Serial Number</span>
                <DebounceInput
                  id="device-serial-number-filter"
                  debounceTimeout={800}
                  type="text"
                  placeholder="Search by Device Serial Number..."
                  value={(filter && filter["device.serialNumber"]?.value) || ""}
                  onChange={onChange}
                  data-key="device.serialNumber"
                  data-com="$lk"
                  key={`${tab}-devSN`}
                />
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>MFR Alert</span>
                <select
                  id="mfr-alert-filter"
                  key={`${tab}-MFR`}
                  onChange={onChange}
                  data-key="manufacturerAlert"
                  data-com="$lk"
                  value={(filter && filter["manufacturerAlert"]?.value) || ""}
                >
                  <option value="">Search by MFR Alert...</option>
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </select>
              </label>
            </div>
          </div>
          <div className="col">
            <h2>Billing</h2>
            <div className="row">
              <label className="inCal">
                <span>Insurance</span>
                <select
                  id="insurance-filter"
                  key={`${tab}-insurance`}
                  onChange={onChange}
                  data-key="patient.insurance.insuranceType"
                  data-com="$eq"
                >
                  {filter && filter["patient.insurance.insuranceType"]?.value ? (
                    showFilteredInfo(
                      insuranceTypes,
                      filter["patient.insurance.insuranceType"]?.value,
                      "Insurance"
                    )
                  ) : (
                    <>
                      <option value="">Search by Insurance...</option>
                      {insuranceTypes.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                      <option value="NONE">NONE</option>
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>30 Day Status</span>
                <select
                  id="30-day-status-filter"
                  key={`${tab}-30`}
                  onChange={onChange}
                  data-key="thirtyDayBillStatus"
                  data-com="$eq"
                >
                  {filter && filter["thirtyDayBillStatus"]?.value ? (
                    showFilteredInfo(thirtyDayStatuses, filter["thirtyDayBillStatus"]?.value, "30 Day Status")
                  ) : (
                    <>
                      <option value="">Search by 30 Day Status...</option>
                      {thirtyDayStatuses.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>90 Day Status</span>
                <select
                  id="90-day-status-filter"
                  key={`${tab}-90`}
                  onChange={onChange}
                  data-key="nintyDayBillStatus"
                  data-com="$eq"
                >
                  {filter && filter["nintyDayBillStatus"]?.value ? (
                    showFilteredInfo(thirtyDayStatuses, filter["nintyDayBillStatus"]?.value, "90 Day Status")
                  ) : (
                    <>
                      <option value="">Search by 90 Day Status...</option>
                      {thirtyDayStatuses.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Auto Calibration</span>
                <select
                  id="auto-calibration-filter"
                  key={`${tab}-calibration`}
                  onChange={onChange}
                  data-key="calibrated"
                  data-com="$eq"
                  value={(filter && filter["calibrated"]?.value) || ""}
                >
                  <option value="">Search by Auto Calibration...</option>
                  <option value={false}>NO</option>
                  <option value={true}>YES</option>
                </select>
              </label>
            </div>
          </div>
          <div className="col">
            <h2>Report</h2>
            <div className="row">
              <label className="inCal">
                <span>Recently Viewed</span>
                <select
                  id="recently-viewed-filter"
                  key={`${tab}-recently`}
                  onChange={onChange}
                  data-key="interrogationAccessHistory"
                  data-com="$eq"
                >
                  {filter && filter["interrogationAccessHistory"]?.value ? (
                    showFilteredInfo(
                      interrogationAccessHistories,
                      filter["interrogationAccessHistory"]?.value,
                      "Recently Viewed"
                    )
                  ) : (
                    <>
                      <option value="">Search by Recently Viewed...</option>
                      {interrogationAccessHistories.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Report Status</span>
                <select
                  id="status-filter"
                  key={`${tab}-status`}
                  onChange={onChange}
                  data-key="interrogationStatus"
                  data-com="$eq"
                >
                  {filter && filter["interrogationStatus"]?.value ? (
                    showFilteredInfo(statuses, filter["interrogationStatus"]?.value, "Status")
                  ) : (
                    <>
                      <option value="">Search by Status...</option>
                      {statuses.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="row">
              <label className="inCal">
                <span>Report ID</span>
                <DebounceInput
                  id="report-id-filter"
                  debounceTimeout={800}
                  type="text"
                  placeholder="Search by Report ID..."
                  value={(filter && filter["id"]?.value) || ""}
                  onChange={onChange}
                  data-key="id"
                  data-com="$eq"
                  key={`${tab}-reportID`}
                />
              </label>
            </div>
            <div className="row">
              <label>
                <span>Report Alert Type</span>
                <MultiSelectV2
                  id="priorityFlag"
                  name="priorityFlag"
                  options={reportAlertTypeOptions}
                  isMulti
                  placeholder="Select Alert Type(s)"
                  value={getPriorityFlagValues()}
                  onChange={onChangeMultiSelect}
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AllReportsFilter;
